import { BmGridState } from "@/components/controls/grid/state/BmGridState";
import userStore from "@/store/modules/userStore";
import router from "@/router/router";
import Vue from "vue";
import {
  createJsonParseReviver,
  createJsonStringifyReplacer,
} from "@/app/storageReplacerReviverUtils";

interface IBmGridStateStorage {
  [key: string]: BmGridState;
}

/**
 * A repository of grid states loaded from a user isolated storage bucket.
 *
 * It is intended that the state objects are passed as reference so when the store is saved the changes are persisted.
 */
class BmGridStateRepository {
  private storage: IBmGridStateStorage = {};
  private activeStorageKey: string | null = null;

  private loadStorageBucket(newStorageKey: string): void {
    this.save();
    let rawObj = JSON.parse(
      localStorage.getItem(newStorageKey) ?? "{}",
      createJsonParseReviver()
    ) as IBmGridStateStorage;
    this.storage = Object.fromEntries(
      Object.entries(rawObj).map(([key, entry]) => {
        return [key, new BmGridState(entry)]; // newing up here to convert plain deserialised data object into a class with methods etc.
      })
    );
    this.activeStorageKey = newStorageKey;
  }

  public save() {
    if (this.activeStorageKey) {
      localStorage.setItem(
        this.activeStorageKey,
        JSON.stringify(this.storage, createJsonStringifyReplacer())
      );
    }
  }

  private static calculateStorageKeyForRequest() {
    return `${
      userStore.state.user?.id.toString() ?? ""
    }-bmgridstates-v2`.replace(/^-/, "");
  }

  /**
   * Loads a grid state keyed from the current route name. Useful if only one grid is expected on the current page (e.g. list pages)
   */
  public getFromCurrentRoute(defaultState?: Partial<BmGridState>) {
    const currentRouteName = router.currentRoute.name;
    if (!currentRouteName) {
      // eslint-disable-next-line no-console
      console.error(
        "Grid state requested for current route, when no route is available."
      );
      return this.getEphemeral(defaultState);
    }

    return this.getFromKey(currentRouteName, defaultState);
  }

  /**
   * Loads a grid state by a user specified key.
   */
  public getFromKey(
    persistenceKey: string,
    defaultState?: Partial<BmGridState>
  ): BmGridState {
    // First ensure the correct storage bucket is loaded
    const storageKey = BmGridStateRepository.calculateStorageKeyForRequest();
    if (this.activeStorageKey !== storageKey) {
      // If not.. load it
      this.loadStorageBucket(storageKey);
    }

    // Now see if we have state for it, and create it if not.
    let state = this.storage[persistenceKey];
    if (!state) {
      state = new BmGridState(defaultState);
      this.storage[persistenceKey] = state;
    }

    // Clear any grid search filters defined in the cache.
    state.gridSearchFilter = "";

    return Vue.observable(state);
  }

  /**
   * Get ephemeral state (not persisted).
   */
  public getEphemeral(defaultState?: Partial<BmGridState>) {
    return Vue.observable(new BmGridState(defaultState));
  }
}

export default new BmGridStateRepository();
